// Win Tool (launcher) campaign wire contract — the ONE schema, the ONE pair of
// fetchers and the ONE pair of react-query keys that launcher, playbooks and
// playhouse all read `GET /api/tools/launcher/campaigns[/:id]` through.
//
// Why this module exists (plans/2026-09-22-playbooks-qa-round8-plan.md, U2):
// Lead Scanner used to cache its own slim `{id, name, eventDate, city, state}`
// view model under the SAME react-query key `['launcher','campaigns']` that
// Playbooks/Playhouse/Launcher read as the full campaign row. A soft-nav out
// of Lead Scanner therefore handed Playbooks a row with no `member_count`, and
// `member_count.toLocaleString()` crashed the page. Two apps may not cache two
// shapes under one key — so the key, the schema and the fetcher now live
// together in one place, every reader goes through them, and Lead Scanner
// caches its view model under its own `['lead-scanner','launcher-campaigns']`
// key. `mini-apps/_shared/__tests__/launcher-campaigns-query-key.test.ts` pins
// that rule against the source tree.
//
// Zod in a module the frontend imports is deliberate and has precedent
// (`_shared/user-controls.ts`, imported by `useCurrentUser.ts`):
// the launcher HTTP API is a trust boundary for every other app
// (`mini-apps/CLAUDE.md` lane 2), and `.claude/team-conventions.md`'s
// "Schema-First Data Boundaries" forbids `as T` / `const x: T = await
// res.json()` there. Reads go through `./fetch-json`.
//
// ── Nullability, verified against Postgres, not against the old interface ──
// `prisma/schema/migrations/20260317_launcher_sfdc_tables/migration.sql`
// (+ `20260630200000_launcher_playhouse_visible`) is the source of truth for
// `launcher_sfdc_campaigns`:
//   NOT NULL : id, name, member_count (DEFAULT 0), created_at, updated_at,
//              playhouse_visible (DEFAULT false — the stored effective flag:
//              COALESCE(playhouse_visible_manual, has-invitees); migration
//              20261019140000_launcher_playhouse_visible_manual_backfill)
//   NULLABLE : type, subtype, start_date (→ `event_date`), end_date, status,
//              description, city, state, capacity, budgeted_cost,
//              event_brief_url, registration_goal, pipeline_goal, arr_goal,
//              target_persona, goal, partner, partner_activation_items,
//              invite_subject, invite_body, members_last_synced_at,
//              last_synced_at, playhouse_visible_manual (the human's switch
//              choice: NULL = never touched, true = on, false = off; same
//              migration 20261019140000_launcher_playhouse_visible_manual_backfill)
// `launcher_status` is not on that table at all: both handlers compute it as
// `COALESCE(s.launcher_status, 'draft')` over `campaign_launcher_state`.
//
// 2026-09-23 PROD BUGFIX: the audit above was wrong. The PATCH route (`z.enum
// (['draft','launched'])`, `mini-apps/launcher/api.ts`) and the launch path
// (`'launched'`) are the only CURRENT writers, but real prod data has an
// eleven-row `'ready'` cohort (`campaign_launcher_state`, all `updated_at` in
// 2026-03/04 — months of legacy data from before this enum was locked down,
// never migrated). `.safeParse` on an ARRAY fails the WHOLE array if even one
// element is invalid, so once this schema shipped, every Playhouse rep saw
// "Couldn't load campaigns" on every load — not transient, not a network
// issue: the underlying 11 rows never change on a refresh. `'ready'` is
// accepted here for READ tolerance of that legacy data; the PATCH route's own
// enum stays `['draft','launched']` on purpose — `'ready'` is a retired write
// value, not one to newly allow.
//
// Two consequences of that audit, both deliberate:
//
//  1. `type` and `status` are NULLABLE in Postgres but every consumer treats
//     them as display strings (`c.type.toLowerCase()` in
//     `PlaybooksHome.tsx` and `LauncherListPage.tsx`, `map.has(c.type)` in
//     `LauncherSettingsPage.tsx`, `c.status || ''` in both detail sidebars).
//     They are parsed as `.nullable()` — a schema stricter than the DB would
//     turn one odd row into an error card for a whole list — and normalized to
//     `''` HERE, once, rather than with a `?? ''` at each of the call sites
//     that would otherwise have to remember. Nothing writes the normalized
//     value back: both detail sidebars diff their form against the same
//     normalized original and send `undefined` for an unchanged field.
//     (The list endpoint cannot actually emit a null `type` — its INNER JOIN
//     on `launcher_campaign_type_config` drops those rows — but the detail
//     endpoint has no such join.)
//  2. `budgeted_cost`, `pipeline_goal` and `arr_goal` are `NUMERIC(12,2)`.
//     `prisma.$queryRaw` hands those back as Prisma `Decimal`s, which
//     JSON-serialize to STRINGS, so the wire type is `number | string`. Both
//     detail sidebars already go through `String(...)`/`parseFloat(...)`, so
//     widening the type here matches what the code always did at runtime.
//     `capacity` and `registration_goal` are plain `INTEGER` (real numbers).
//
// Required-vs-optional follows the two handlers: the nine fields the list
// SELECT always projects are required, everything only the detail `SELECT c.*`
// adds is optional. `.passthrough()` keeps any column added to the mirror
// later instead of silently stripping it (at the cost of an `unknown` index
// signature on the inferred type — accepted: a mistyped field is `unknown`,
// which still fails at the point of use).
import { z } from 'zod';

import { fetchJson } from './fetch-json';

/**
 * `campaign_launcher_state.launcher_status`, COALESCEd to `'draft'`.
 * `'ready'` is a retired value (no current writer can produce it — see the
 * module doc's 2026-09-23 note) kept here only so the ~11 real legacy rows
 * that still carry it don't fail the whole list's parse.
 */
export const LauncherStatusSchema = z.enum(['draft', 'ready', 'launched']);

/**
 * A nullable Postgres TEXT column that every consumer reads as a display
 * string. Accepts `null` on the wire, hands back `''`.
 */
const displayText = z
  .string()
  .nullable()
  .transform((v) => v ?? '');

/** `NUMERIC(12,2)` — a Prisma `Decimal` on the wire, i.e. a JSON string. */
const wireDecimal = z.union([z.number(), z.string()]).nullable().optional();

const nullableText = z.string().nullable().optional();
const nullableInt = z.number().nullable().optional();

/**
 * `GET /api/tools/launcher/campaigns` (list) and `/campaigns/:id` (detail) —
 * `mini-apps/launcher/api.ts#createListCampaigns` / `#createGetCampaign`.
 */
export const LauncherCampaignSchema = z
  .object({
    // ── Always projected by the list SELECT ──
    id: z.string(),
    name: z.string(),
    type: displayText,
    subtype: z.string().nullable(),
    /** `c.start_date`, run through the handlers' `formatDate` (`YYYY-MM-DD`). */
    event_date: z.string().nullable(),
    status: displayText,
    member_count: z.number().int(),
    launcher_status: LauncherStatusSchema,
    members_last_synced_at: z.string().nullable(),
    // ── Also on the list SELECT, but optional so existing fixtures and the
    //    narrower callers that only build a list row keep compiling ──
    city: nullableText,
    state: nullableText,
    target_persona: nullableText,
    invite_subject: nullableText,
    invite_body: nullableText,
    playhouse_visible: z.boolean().optional(),
    // ── Detail-only (`SELECT c.*`) ──
    start_date: nullableText,
    end_date: nullableText,
    description: nullableText,
    capacity: nullableInt,
    budgeted_cost: wireDecimal,
    event_brief_url: nullableText,
    registration_goal: nullableInt,
    pipeline_goal: wireDecimal,
    arr_goal: wireDecimal,
    goal: nullableText,
    partner: nullableText,
    partner_activation_items: nullableText,
    last_synced_at: nullableText,
    created_at: nullableText,
    updated_at: nullableText,
  })
  .passthrough();

export const LauncherCampaignsResponseSchema = z.array(LauncherCampaignSchema);

export type Campaign = z.infer<typeof LauncherCampaignSchema>;
export type LauncherStatus = z.infer<typeof LauncherStatusSchema>;

const CAMPAIGNS_PATH = '/api/tools/launcher/campaigns';

/**
 * The ONE key every app reads launcher's campaign list under. Enforced by
 * `__tests__/launcher-campaigns-query-key.test.ts`: a `useQuery`/`fetchQuery`
 * on this key must use `fetchLauncherCampaigns` as its `queryFn`.
 */
export const LAUNCHER_CAMPAIGNS_QUERY_KEY = ['launcher', 'campaigns'] as const;

/** The detail key — the list key plus the campaign's SFDC id. */
export const launcherCampaignQueryKey = (id: string) => ['launcher', 'campaigns', id] as const;

type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

/** Launcher's campaign list, parsed. Rejects (never casts) on a wrong shape. */
export function fetchLauncherCampaigns(apiFetch: ApiFetch): Promise<Campaign[]> {
  return fetchJson(apiFetch, LauncherCampaignsResponseSchema, CAMPAIGNS_PATH);
}

/** One launcher campaign, parsed. Rejects (never casts) on a wrong shape. */
export function fetchLauncherCampaign(apiFetch: ApiFetch, id: string): Promise<Campaign> {
  return fetchJson(apiFetch, LauncherCampaignSchema, `${CAMPAIGNS_PATH}/${encodeURIComponent(id)}`);
}

// ── Patch Campaign Details ───────────────────────────────────────────────
//
// Moved from `mini-apps/launcher/event-manager-client.ts` (U6) so
// `mini-apps/_shared/components/CampaignDetailSidebar.tsx` can import
// `PatchCampaignFields`/`patchCampaignDetails` without reaching into
// `mini-apps/launcher/**` (a lint-forbidden import direction for `_shared`).
// `event-manager-client.ts` re-exports both from here.

export interface PatchCampaignFields {
  description?: string | null;
  status?: string;
  start_date?: string | null;
  end_date?: string | null;
  city?: string | null;
  state?: string | null;
  capacity?: number | null;
  budgeted_cost?: number | null;
  event_brief_url?: string | null;
  target_persona?: string | null;
  goal?: string | null;
  registration_goal?: number | null;
  pipeline_goal?: number | null;
  arr_goal?: number | null;
  partner?: string | null;
  partner_activation_items?: string | null;
  invite_subject?: string | null;
  invite_body?: string | null;
  playhouse_visible?: boolean;
}

export async function patchCampaignDetails(
  apiFetch: ApiFetch,
  campaignId: string,
  fields: PatchCampaignFields,
): Promise<void> {
  // The response body is not read: no caller uses it, and a committed PATCH
  // must not turn into a failure because its echo body is unexpected.
  await apiFetch(`${CAMPAIGNS_PATH}/${encodeURIComponent(campaignId)}/details`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
}
